import { useState } from "react";
import { motion } from "framer-motion";
import { Button } from "../components/ui/Button.js";
import { Input } from "../components/ui/Input.js";
import { Card } from "../components/ui/Card.js";
import { Badge } from "../components/ui/Badge.js";
import { Textarea } from "../components/ui/Textarea.js";
import { useToast } from "../hooks/use-toast.js";
import { useMutation } from "@apollo/client";
import { ADD_WAITLIST_ENTRY } from "../lib/graphqlOperations.js";
import { Navbar } from "../components/layout/Navbar.js";
import Footer from "../components/layout/Footer.js";
import {
  Mail,
  MapPin,
  MessageSquare,
  Send,
  Check,
  Clock,
  ArrowRight,
  Store,
  Users,
  Headphones,
  Building2,
} from "lucide-react";

const contactReasons = [
  { value: "general", label: "General Inquiry" },
  { value: "customer", label: "Customer Support" },
  { value: "vendor", label: "Vendor Partnership" },
  { value: "investor", label: "Investment Interest" },
  { value: "press", label: "Press & Media" },
  { value: "careers", label: "Careers" },
];

const contactInfo = [
  {
    icon: Mail,
    title: "Email Us",
    value: "q@quro.today",
    description: "We reply within 24 hours",
    href: "mailto:q@quro.today",
  },
  {
    icon: MapPin,
    title: "Registered Office",
    value: "Quick Retail Online Private Limited",
    description: "24-21, Ashoknagar, Ramachandra Puram, Hyderabad-502032",
    href: null,
  },
  {
    icon: Building2,
    title: "HQ Office",
    value: "Suite 406, 4th floor, MyScape Weave",
    description: "Financial District, Nankramaguda, Hyderabad-500032",
    href: null,
  },
];

const faqs = [
  {
    question: "When will Quro launch in my city?",
    answer:
      "We're launching city by city. Join the waitlist and we'll notify you as soon as we're live in your area.",
  },
  {
    question: "How do I become a vendor on Quro?",
    answer:
      "Visit our Vendors page and sign up with just your phone number. Setup is completely free for early vendors.",
  },
  {
    question: "What is the free delivery offer?",
    answer:
      "Early waitlist members get 1 full year of free deliveries once Quro launches. No catch, no credit card required.",
  },
  {
    question: "How does multi-vendor ordering work?",
    answer:
      "You can add items from multiple local vendors into a single cart and get everything delivered in one trip.",
  },
];

export default function ContactUs() {
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [subject, setSubject] = useState("");
  const [messageText, setMessageText] = useState("");
  const [isSubmitted, setIsSubmitted] = useState(false);
  const { toast } = useToast();

  const [addWaitlistEntry, { loading: submitPending }] = useMutation(
    ADD_WAITLIST_ENTRY,
    {
      onCompleted: (data) => {
        if (data?.addWaitlistEntry?.success) {
          setIsSubmitted(true);
        } else {
          const msg =
            data?.addWaitlistEntry?.message ||
            "Something went wrong. Please try again.";
          toast({
            title: "Something went wrong",
            description: msg,
            variant: "destructive",
          });
        }
      },
      onError: (error) => {
        toast({
          title: "Something went wrong",
          description:
            error.message || "Please check your details and try again.",
          variant: "destructive",
        });
      },
    }
  );

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!name || !phone) return;

    const cleanPhone = phone.replace(/\s+/g, "").replace(/\D/g, "").replace(/^(\+?91|0)/, "").slice(-10);
    if (cleanPhone.length !== 10) {
      toast({
        title: "Invalid Phone",
        description: "Please enter a valid 10-digit phone number.",
        variant: "destructive",
      });
      return;
    }

    addWaitlistEntry({
      variables: {
        name,
        phone: cleanPhone,
        email: email || undefined,
        entryType: subject || "general",
        message: messageText || undefined,
        isContact: true,
      },
    });
  };

  return (
    <div className="min-h-screen bg-background text-foreground overflow-x-hidden">
      <Navbar />

      {/* Animated Background Blobs */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none z-0">
        <motion.div
          animate={{
            x: [0, 100, 0],
            y: [0, 50, 0],
            scale: [1, 1.2, 1],
          }}
          transition={{ duration: 24, repeat: Infinity, ease: "linear" }}
          className="absolute -top-[10%] -left-[10%] w-[40%] h-[40%] bg-primary/5 rounded-full blur-[120px]"
        />
        <motion.div
          animate={{
            x: [0, -60, 0],
            y: [0, 120, 0],
            scale: [1, 1.4, 1],
          }}
          transition={{ duration: 30, repeat: Infinity, ease: "linear" }}
          className="absolute top-[30%] -right-[5%] w-[35%] h-[35%] bg-accent/10 rounded-full blur-[120px]"
        />
      </div>

      <div className="relative z-10">
        {/* Hero */}
      <section className="relative pt-24 pb-16 md:pt-32 md:pb-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-accent/10" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center max-w-4xl mx-auto space-y-8"
          >
            <Badge variant="secondary" className="px-5 py-2.5 bg-accent/50 backdrop-blur-md text-accent-foreground border-accent/20 font-bold">
              <Headphones className="w-5 h-5 mr-3 text-primary" />
              We're Here for You
            </Badge>
            <h1 className="font-display text-5xl sm:text-6xl md:text-7xl font-bold leading-[1.1] tracking-tight">
              Get in Touch with{" "}
              <span className="text-primary relative inline-block">
                Team Quro
                <svg className="absolute -bottom-2 left-0 w-full" viewBox="0 0 358 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <motion.path
                    initial={{ pathLength: 0 }}
                    animate={{ pathLength: 1 }}
                    transition={{ duration: 1, delay: 0.5 }}
                    d="M3 9C118.5 3 239.5 3 355 9"
                    stroke="currentColor"
                    strokeWidth="6"
                    strokeLinecap="round"
                  />
                </svg>
              </span>
            </h1>
            <p className="text-xl md:text-2xl text-muted-foreground/80 max-w-3xl mx-auto leading-relaxed">
              Whether you're a customer, vendor, investor, or just curious —
              we'd love to hear from you. Experience support that actually cares.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Main Content: Form + FAQ */}
      <section id="contact-form" className="py-10 md:py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-5 gap-10 lg:gap-16 items-start">

            {/* Form */}
            <div className="lg:col-span-3">
              <Card className="p-8 md:p-10 border-border/50 shadow-md">
                {!isSubmitted ? (
                  <>
                    <div className="mb-10">
                      <div className="flex items-center gap-3 mb-3">
                        <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                          <MessageSquare className="w-6 h-6 text-primary" />
                        </div>
                        <h2 className="font-display text-3xl font-bold">
                          Send a Message
                        </h2>
                      </div>
                      <p className="text-muted-foreground text-sm">
                        Fill out the details below and we'll bridge the gap within 24 hours.
                      </p>
                    </div>

                    <form onSubmit={handleSubmit} className="space-y-5">
                      <div className="grid sm:grid-cols-2 gap-6">
                        <div className="space-y-3">
                          <label className="text-sm font-bold ml-1">
                            Full Name <span className="text-primary">*</span>
                          </label>
                          <Input
                            id="contact-name"
                            type="text"
                            placeholder="Your name"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            className="h-14 rounded-2xl border-border/60 bg-background/50 backdrop-blur-md shadow-soft"
                          />
                        </div>
                        <div className="space-y-3">
                          <label className="text-sm font-bold ml-1">
                            Phone Number{" "}
                            <span className="text-primary">*</span>
                          </label>
                          <Input
                            id="contact-phone"
                            type="tel"
                            placeholder="+91 9999999999"
                            value={phone}
                            onChange={(e) => setPhone(e.target.value)}
                            className="h-14 rounded-2xl border-border/60 bg-background/50 backdrop-blur-md shadow-soft"
                          />
                        </div>
                      </div>

                      <div className="space-y-3">
                        <label className="text-sm font-bold ml-1">
                          Email Address
                          <span className="text-muted-foreground text-xs font-normal ml-2">
                            (optional)
                          </span>
                        </label>
                        <Input
                          id="contact-email"
                          type="email"
                          placeholder="you@email.com"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          className="h-14 rounded-2xl border-border/60 bg-background/50 backdrop-blur-md shadow-soft"
                        />
                      </div>

                      <div className="space-y-2">
                        <label className="text-sm font-medium">
                          What's this about?
                        </label>
                        <div className="flex flex-wrap gap-2">
                          {contactReasons.map((reason) => (
                            <button
                              key={reason.value}
                              type="button"
                              onClick={() => setSubject(reason.value)}
                              className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-all duration-200 ${
                                subject === reason.value
                                  ? "bg-primary text-primary-foreground border-primary"
                                  : "bg-background text-muted-foreground border-border hover:border-primary hover:text-primary"
                              }`}
                            >
                              {reason.label}
                            </button>
                          ))}
                        </div>
                      </div>

                      <div className="space-y-2">
                        <label className="text-sm font-medium">
                          Message
                          <span className="text-muted-foreground text-xs ml-1">
                            (optional)
                          </span>
                        </label>
                        <Textarea
                          id="contact-message"
                          placeholder="Tell us more about what you need..."
                          value={messageText}
                          onChange={(e) => setMessageText(e.target.value)}
                          rows={4}
                        />
                      </div>

                       <Button
                        id="contact-submit"
                        type="submit"
                        size="lg"
                        className="w-full h-15 h-14 rounded-2xl text-lg font-bold shadow-glow shadow-primary/20 hover:shadow-primary/40 transition-all duration-300 hover:-translate-y-1"
                        disabled={submitPending || !name || !phone}
                      >
                        {submitPending ? (
                          "Sending Message..."
                        ) : (
                          <>
                            Send Message
                            <Send className="w-5 h-5 ml-2" />
                          </>
                        )}
                      </Button>

                      <p className="text-xs text-center text-muted-foreground">
                        By submitting, you agree to our{" "}
                        <a
                          href="/privacy"
                          className="underline hover:text-foreground"
                        >
                          Privacy Policy
                        </a>
                        .
                      </p>
                    </form>
                  </>
                ) : (
                  <div className="text-center py-10 space-y-5">
                    <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto">
                      <Check className="w-8 h-8 text-primary" />
                    </div>
                    <div className="space-y-2">
                      <h3 className="text-xl font-semibold">Message Sent!</h3>
                      <p className="text-muted-foreground max-w-sm mx-auto">
                        Thanks for reaching out, {name}! We've received your
                        message and will get back to you within 24 hours.
                      </p>
                    </div>
                    <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
                      <Clock className="w-4 h-4" />
                      <span>Typical response time: under 24 hours</span>
                    </div>
                    <Button
                      variant="outline"
                      onClick={() => {
                        setIsSubmitted(false);
                        setName("");
                        setPhone("");
                        setEmail("");
                        setSubject("");
                        setMessageText("");
                      }}
                    >
                      Send Another Message
                    </Button>
                  </div>
                )}
              </Card>
            </div>

            {/* FAQ */}
            <div className="lg:col-span-2 space-y-6">
              <div>
                <h2 className="font-display text-2xl font-bold mb-2">
                  Frequently Asked Questions
                </h2>
                <p className="text-muted-foreground text-sm">
                  Quick answers to common questions.
                </p>
              </div>
              <div className="space-y-4">
                {faqs.map((faq, i) => (
                  <Card key={i} className="p-5 border-border/50 shadow-sm">
                    <h3 className="font-semibold text-sm mb-2">
                      {faq.question}
                    </h3>
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      {faq.answer}
                    </p>
                  </Card>
                ))}
              </div>

              {/* Quick links */}
              <Card className="p-5 border-primary/20 bg-primary/5">
                <h3 className="font-semibold mb-3 flex items-center gap-2">
                  <Users className="w-4 h-4 text-primary" />
                  Looking for something specific?
                </h3>
                <div className="space-y-2">
                  <a
                    href="/vendors"
                    className="flex items-center justify-between text-sm text-muted-foreground hover:text-primary transition-colors py-1"
                  >
                    <span className="flex items-center gap-2">
                      <Store className="w-3.5 h-3.5" /> Join as a Vendor
                    </span>
                    <ArrowRight className="w-3.5 h-3.5" />
                  </a>
                  <a
                    href="/investors"
                    className="flex items-center justify-between text-sm text-muted-foreground hover:text-primary transition-colors py-1"
                  >
                    <span className="flex items-center gap-2">
                      <Building2 className="w-3.5 h-3.5" /> Investor Relations
                    </span>
                    <ArrowRight className="w-3.5 h-3.5" />
                  </a>
                  <a
                    href="/"
                    className="flex items-center justify-between text-sm text-muted-foreground hover:text-primary transition-colors py-1"
                  >
                    <span className="flex items-center gap-2">
                      <Users className="w-3.5 h-3.5" /> Customer Waitlist
                    </span>
                    <ArrowRight className="w-3.5 h-3.5" />
                  </a>
                </div>
              </Card>
            </div>
          </div>
        </div>
      </section>

      <Footer />
      </div>
    </div>
  );
}
